import { apiClient } from './api';

export interface Staff {
  id: string;
  userId: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth?: string;
  hireDate: string;
  position: string;
  department: string;
  skills: string[];
  certifications: any[];
  workingHours: {
    [key: string]: {
      start: string;
      end: string;
      isWorking: boolean;
    };
  };
  hourlyRate?: number;
  commissionRate?: number;
  isActive: boolean;
  salonId: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    role: string;
    isActive: boolean;
    lastLogin?: string;
  };
}

export interface CreateStaffData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth?: string;
  hireDate: string;
  position: string;
  department: string;
  skills: string[];
  certifications: any[];
  workingHours: {
    [key: string]: {
      start: string;
      end: string;
      isWorking: boolean;
    };
  };
  hourlyRate?: number;
  commissionRate?: number;
  role: 'STAFF' | 'MANAGER' | 'RECEPTIONIST';
  password: string;
}

export interface UpdateStaffData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  hireDate?: string;
  position?: string;
  department?: string;
  skills?: string[];
  certifications?: any[];
  workingHours?: {
    [key: string]: {
      start: string;
      end: string;
      isWorking: boolean;
    };
  };
  hourlyRate?: number;
  commissionRate?: number;
  role?: 'STAFF' | 'MANAGER' | 'RECEPTIONIST';
}

export interface StaffFilters {
  page?: number;
  limit?: number;
  search?: string;
  department?: string;
  position?: string;
  isActive?: boolean;
}

export interface StaffResponse {
  staff: Staff[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface StaffStats {
  totalStaff: number;
  activeStaff: number;
  inactiveStaff: number;
  departmentBreakdown: {
    department: string;
    count: number;
  }[];
  positionBreakdown: {
    position: string;
    count: number;
  }[];
}

class StaffService {
  private baseUrl = '/staff';

  async getStaff(filters: StaffFilters = {}): Promise<StaffResponse> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await apiClient.get(`${this.baseUrl}?${params.toString()}`);
    return response;
  }

  async getStaffById(id: string): Promise<Staff> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response;
  }

  async createStaff(data: CreateStaffData): Promise<Staff> {
    const response = await apiClient.post(this.baseUrl, data);
    return response;
  }

  async updateStaff(id: string, data: UpdateStaffData): Promise<Staff> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response;
  }

  async updateStaffPassword(
    id: string,
    data: { currentPassword: string; newPassword: string }
  ): Promise<void> {
    await apiClient.put(`${this.baseUrl}/${id}/password`, data);
  }

  async toggleStaffStatus(id: string): Promise<Staff> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/toggle-status`);
    return response;
  }

  async deleteStaff(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async getStaffPerformance(id: string, period?: string): Promise<any[]> {
    const params = period ? `?period=${period}` : '';
    const response = await apiClient.get(`${this.baseUrl}/${id}/performance${params}`);
    return response;
  }

  async getStaffStats(): Promise<StaffStats> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response;
  }
}

export const staffService = new StaffService();
